import React, { useRef, useCallback, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTheme } from "../../context/ThemeContext";
import eventIcon from "../../assets/icons/Calendar_Days.svg";

const formatEventTime = (isoString) => {
  try {
    const date = new Date(isoString);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'Invalid time';
    }

    // Format hours and minutes with am/pm
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting time:', error);
    return 'Invalid time';
  }
};

/**
 * List view component for events page
 * Displays events in a timeline format with dates on the left and events on the right
 * @param {Object} props Component props
 * @param {Array} props.availableDates Array of available dates
 * @param {Object} props.eventsByDate Events organized by date
 * @param {boolean} props.loading Loading state
 * @param {string} props.error Error message if any
 * @param {string} props.activeDate Currently active date
 * @param {Function} props.setActiveDate Function to set active date
 * @param {Function} props.formatDisplayDate Function to format date for display
 * @param {Function} props.formatTimelineDate Function to format date for timeline
 * @param {Function} props.isToday Function to check if a date is today
 * @param {Object} props.scrollRefs References for scrolling functionality
 * @param {Function} props.handleTimelineClick Function to handle timeline click
 * @returns {JSX.Element} List view component
 */
const ListView = ({
  availableDates,
  eventsByDate,
  loading,
  error,
  activeDate,
  setActiveDate,
  formatDisplayDate,
  formatTimelineDate,
  isToday,
  scrollRefs,
  handleTimelineClick
}) => {
  const { theme } = useTheme();
  
  // Animation variants
  const indicatorVariants = {
    initial: { opacity: 0, x: -4 },
    animate: { opacity: 1, x: 0 }
  };

  useEffect(() => {
    const todayDate = availableDates.find((date) => isToday(date));
    if (todayDate && scrollRefs.timelineRefs.current[todayDate]?.current) {
      scrollRefs.timelineRefs.current[todayDate].current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      // Optionally set the active date too
      if (setActiveDate) {
        setActiveDate(todayDate);
      }
    }
  }, [availableDates, scrollRefs.timelineRefs, isToday, setActiveDate]);


  // If no dates available, show message
  if (availableDates.length === 0 && !loading && !error) {
    return (
      <div className="flex-1 flex justify-center items-center text-gray-500 dark:text-gray-400">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
        >
          No events scheduled.
        </motion.p>
      </div>
    );
  }

  return (
    <>
      {/* Stabilized Timeline Navigation */}
      <div
        ref={scrollRefs.timelineContainerRef}
        className={`w-24 md:w-28 ${theme.glassCard} flex-shrink-0 mr-2 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700 transition-colors duration-300`}
        style={{ scrollbarWidth: 'thin' }}
      >
        <div className="py-1 px-1">
          {availableDates.map((date) => {
            const dateObj = formatTimelineDate(date);
            const isActive = activeDate === date;
            const isDateToday = isToday(date);

            return (
              <button
                key={date}
                ref={scrollRefs.timelineRefs.current[date]}
                onClick={() => handleTimelineClick(date)}
                className={`w-full flex flex-col items-center py-4 px-1 mb-1 rounded-md relative focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900 transition-all duration-200 ease-out ${
                  isDateToday && !isActive
                    ? 'bg-blue-50 dark:bg-blue-900/20'
                    : !isDateToday && !isActive
                      ? 'hover:bg-gray-100 dark:hover:bg-gray-800/60'
                      : ''
                }`}
              >
                {/* Stable Active Indicator - Use fixed positioning to avoid layout shifts */}
                {isActive && (
                  <motion.div
                    className="absolute inset-0 bg-blue-500/10 dark:bg-blue-500/20 rounded-md"
                    variants={indicatorVariants}
                    initial="initial"
                    animate="animate"
                    transition={{
                      type: "tween",
                      duration: 0.2,
                    }}
                    style={{ originX: 0 }}
                    layoutId="activeTimelineIndicator"
                    ref={scrollRefs.activeIndicatorRef}
                  />
                )}

                {/* Date Content */}
                <div className="relative z-10 flex items-baseline font-Poppins gap-1">
                  {isDateToday && (
                      <div
                          className={`absolute -left-8 w-1 h-full rounded-full transition-colors duration-200 ${
                              isActive ? 'bg-blue-500' : 'bg-blue-300 dark:bg-blue-700'
                          }`}
                      />
                  )}
                  <span
                      className={`font-semibold mt-0.5 transition-colors duration-200 ${
                          isActive
                              ? 'text-blue-700 dark:text-blue-300'
                              : 'text-gray-800 dark:text-gray-200'
                      }`}
                  >
                    {dateObj.day}
                  </span>
                  <span
                      className={`text-xs font-medium transition-colors duration-200 ${
                          isActive
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-gray-500 dark:text-gray-400'
                      }`}
                  >
                    {dateObj.weekday}
                  </span>

                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Events Display */}
      <div
          ref={scrollRefs.scrollContainerRef}
          className={`flex-1 overflow-y-auto ${theme.glassCard} overflow-clip scrollbar-thin scrollbar-thumb-gray-300 dark:scrollbar-thumb-gray-700`}
          style={{scrollbarWidth: 'thin'}}
      >
        {availableDates.map((date) => {
          const events = eventsByDate[date] || [];
          if (events.length === 0) return null;
          const isDateToday = isToday(date);
          const isActive = activeDate === date;

          return (
            <div
              key={date}
              ref={scrollRefs.contentRefs.current[date]}
              className=""
              id={`content-${date}`}
            >
              {/* Sticky Date Header - Simplified styling to improve stability */}
              <div
                className={`rounded-t-md sticky top-0 z-10 px-4 py-3 border-b backdrop-blur-md transition-colors duration-300 ${
                  isActive
                    ? `${theme.glassActive} border-blue-200 dark:border-blue-800/50` 
                    : `${theme.glassHeader} border-white/30 dark:border-white/20`
                }`}
              >
                <h2
                  className={`text-lg font-semibold transition-colors duration-300 ${
                    isActive
                      ? 'text-blue-700 dark:text-blue-300'
                      : theme.text.primary
                  }`}
                >
                  {formatDisplayDate(date)}
                </h2>
              </div>

              <div className="px-4">
                {/* Events List - Simplified animations to reduce shakiness */}
                <div className="space-y-4 mt-4">
                  {events.map((event, eventIndex) => (
                    <motion.div
                      key={event.id || `${date}-${eventIndex}`}
                      className={`${theme.glassCard} shadow-md overflow-hidden flex transition-colors duration-300 ${
                        isActive
                          ? 'border-blue-100 dark:border-blue-900/30'
                          : ''
                      }`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: eventIndex * 0.05,
                        duration: 0.3
                      }}
                    >
                      {/* Time/Image indicator */}
                      <div className={`w-24 md:w-28 flex-shrink-0 ${theme.glassHeader} border-r border-white/30 dark:border-white/20 flex flex-col justify-center items-center transition-colors duration-300 overflow-hidden`}>
                        {event.imageUrl ? (
                          <img
                            src={"https://www.middlebury.edu" + event.imageUrl}
                            className="w-full h-full object-cover"
                            alt={event.title || "Event"}
                          />
                        ) : (
                          <div className="flex flex-col items-center justify-center h-full w-full p-2">
                            <img
                              src={eventIcon}
                              className="w-6 h-6 opacity-30 mb-1 dark:invert"
                              alt="Event icon"
                            />
                          </div>
                        )}
                      </div>

                      {/* Event details */}
                      <div className="flex-1 p-4">
                        <h3 className={`text-base font-semibold ${theme.text.primary}`}>
                          {event.title}
                        </h3>
                        <p className={`text-sm ${theme.text.secondary} mt-1`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block mr-1 -mt-0.5 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                          </svg>
                          {event.location || 'N/A'}
                        </p>
                        {/* Added time display with clock icon */}
                        <p className={`text-sm ${theme.text.secondary} mt-1`}>
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 inline-block mr-1 -mt-0.5 text-gray-400 dark:text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          {event.datetime && event.endDateTime ?
                            `${formatEventTime(event.datetime)} - ${formatEventTime(event.endDateTime)}` :
                            event.datetime ?
                              `${formatEventTime(event.datetime)}` :
                              'Time not specified'}
                        </p>
                        {event.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 line-clamp-2">
                            {event.description}
                          </p>
                        )}
                        {event.eventUrl && (
                          <a
                            href={"https://www.middlebury.edu" + event.eventUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 dark:text-blue-400 text-sm hover:underline font-medium inline-block mt-2 group"
                          >
                            View Details <span className="transition-transform duration-200 inline-block group-hover:translate-x-1">→</span>
                          </a>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
        {/* Add some bottom padding */}
        <div className="h-16"></div>
      </div>
    </>
  );
};

export default ListView;